export default class StaticSlideshow {
  constructor({ el, data }) {
    // Elements
    this.el = el;
    this.slideshowWrapper = this.el.querySelector('.static-slideshow-wrapper');
    this.slideshowContent = this.el.querySelector('[data-slideshow-content]');
    this.slides = this.el.querySelectorAll('[data-slide]');
    this.headerNavigation = document.querySelector('[data-main-header]');
    this.slideNavigation = this.el.querySelectorAll('.static-slideshow-navigation');
    this.slidePagination = this.el.querySelector('.static-slideshow-pagination');
    this.activeSlide = null;

    // Variables
    this.fullWindowWidth = window.innerWidth;
    this.fullWindowHeight = window.innerHeight;

    // Settings
    this.autoplaySpeed = data.autoplay_speed;
    this.slideshowLayout = data.layout;
    this.autoplayEnabled = data.enable_autoplay;

    // Event listeners
    this.events = [];
    if (this.slideNavigation.length) {
      this._registerEvent(this.slideNavigation[0], 'click', (e) => {
        e.preventDefault();
        this._previousSlide();
      });

      this._registerEvent(this.slideNavigation[1], 'click', (e) => {
        e.preventDefault();
        this._nextSlide();
      });

      this._registerEvent(this.slidePagination, 'click', (e) => {
        e.preventDefault();
        const { parentNode } = e.target;
        if (parentNode.classList.contains('pagination-list-item')) {
          this._specificSlide(parentNode);
        }
      });
    }

    this._registerEvent(this.el, 'mouseenter', () => this._pauseLoop());
    this._registerEvent(this.el, 'mouseleave', () => this._startLoop());
    this._registerEvent(this.el, 'rimg:load', () => this._setActiveSlideHeight());
    this._registerEvent(window, 'resize', () => {
      // This is to prevent resize on scroll of Android and iOS when the address bar is hidden.
      if (window.innerWidth === this.fullWindowWidth) return;
      this.fullWindowWidth = window.innerWidth;
      this.fullWindowHeight = window.innerHeight;
      this._setActiveSlideHeight();
    });

    // Initializers
    this._setupSlides();
  }

  _setupSlides() {
    this.activeSlide = this.slides[0];
    this.activeSlide.classList.add('active');

    this._setSlideshowClasses();
    this._setActiveSlideHeight();

    this.el.classList.add('slides-ready');

    this._startLoop();
  }

  _setActiveSlideHeight() {
    const slideText = this.activeSlide.querySelector('.slide-text');
    const slideImage = this.activeSlide.querySelector('.slide-image');

    const slidePadding = this.slidePagination
      ? this.slidePagination.getBoundingClientRect().height * 2
      : 60;
    const textHeight = slideText ? slideText.getBoundingClientRect().height + slidePadding : 0;
    const imageHeight = slideImage.getBoundingClientRect().height;

    if (!imageHeight) return;

    let slideHeight;

    if (this.slideshowLayout === 'full-window') {
      const headerHeight = this.headerNavigation.getBoundingClientRect().height;

      // If there is too much text, the height of the slide should be more than the full
      // height of the window.
      if (textHeight > window.innerHeight - headerHeight) {
        slideHeight = window.innerWidth > 720
          ? textHeight + (headerHeight * 2)
          : textHeight;
      } else {
        slideHeight = window.innerWidth > 720
          ? this.fullWindowHeight
          : this.fullWindowHeight - this.headerNavigation.getBoundingClientRect().height;
      }

      const announcementBarEl = document.querySelector('.pxs-announcement-bar');

      // If is full window, subtract announcement bar height
      if (announcementBarEl) {
        slideHeight -= announcementBarEl.getBoundingClientRect().height;
      }

      this.slideshowWrapper.style.height = `${slideHeight}px`;

      // Update height of all slides since they should all be the same height.
      // This is to ensure all lazy loading of images crop the same.
      for (let i = 0; i < this.slides.length; i++) {
        this.slides[i].style.height = `${slideHeight}px`;
      }
    } else {
      // Minimum height is height of text, or image, which ever is larger
      slideHeight = Math.max(textHeight, imageHeight);
      this.slideshowWrapper.style.height = `${slideHeight}px`;
      this.activeSlide.style.height = `${slideHeight}px`;
    }
  }

  _setSlideshowClasses() {
    const slideTextStyle = this.activeSlide.getAttribute('data-slide-text');
    document.body.classList.remove('slide-color-light');
    document.body.classList.remove('slide-color-dark');
    document.body.classList.add(`slide-color-${slideTextStyle}`);
  }

  _specificSlide(target) {
    if (this.sliding) return;
    if (!target.classList.contains('active')) {
      const nextSlideID = target.getAttribute('data-slide-id');
      this._showNewSlide('next', nextSlideID);
    }
  }

  _previousSlide() {
    if (this.sliding) return;
    this._showNewSlide('prev');
  }

  _nextSlide() {
    if (this.sliding) return;
    this._showNewSlide('next');
  }

  _getIndexOfSlide(slide) {
    for (let i = 0; i < this.slides.length; i++) {
      if (this.slides[i] === slide) return i;
    }

    return -1;
  }

  _showNewSlide(type, specificSlideID) {
    this.sliding = true;

    if (this.slides.length === 1 || this.activeSlide.id === specificSlideID) {
      this.sliding = false;
      return;
    }

    const direction = type === 'next' ? 'left' : 'right';
    const activeSlideIndex = this._getIndexOfSlide(this.activeSlide);

    if (specificSlideID) {
      this.nextSlide = this.el.querySelector(`#${specificSlideID}`);
    } else if (type === 'next') {
      this.nextSlide = activeSlideIndex + 1 >= this.slides.length
        ? this.slides[0]
        : this.slides[activeSlideIndex + 1];
    } else {
      this.nextSlide = activeSlideIndex - 1 < 0
        ? this.slides[this.slides.length - 1]
        : this.slides[activeSlideIndex - 1];
    }

    if (this.activeSlide.id === this.nextSlide.id) return;

    this.nextSlide.classList.add(type);

    // force reflow for slide effect
    this.nextSlide.offsetWidth; // eslint-disable-line

    this.activeSlide.classList.add(direction);
    this.nextSlide.classList.add(direction);

    this.previousSlide = this.activeSlide;

    this._slideListener = this._registerEvent(this.nextSlide,
      'transitionend', () => this._slideTransitionEnd()).listener;

    this.activeSlide = this.nextSlide;
    this._setActiveSlideHeight();
    this._setSlideshowClasses();
    this._updateSlidePagination();
  }

  _slideTransitionEnd() {
    this.nextSlide.classList.remove('next');
    this.nextSlide.classList.remove('prev');
    this.nextSlide.classList.remove('right');
    this.nextSlide.classList.remove('left');
    this.nextSlide.classList.add('active');
    this.previousSlide.classList.remove('active');
    this.previousSlide.classList.remove('right');
    this.previousSlide.classList.remove('left');
    this.previousSlide = null;

    this._unregisterEvent(this.nextSlide, 'transitionend', this._slideListener);

    this.nextSlide = null;
    this.sliding = false;
  }

  _startLoop() {
    if (!this.autoplayEnabled || this.slidesLocked) return;

    this.autoplayInterval = setInterval(() => this._nextSlide(), this.autoplaySpeed);
  }

  _pauseLoop() {
    if (this.autoplayInterval == null) return;
    clearInterval(this.autoplayInterval);
  }

  _updateSlidePagination() {
    const index = this._getIndexOfSlide(this.activeSlide);
    this.slidePagination.querySelector('.active').classList.remove('active');
    this.slidePagination.querySelectorAll('.pagination-list-item')[index].classList.add('active');
  }

  _lockSlide(slideID) {
    this.slidesLocked = true;
    this._pauseLoop();
    this._showNewSlide('next', slideID);
  }

  _unlockSlide() {
    this.slidesLocked = false;
    this._startLoop();
  }

  onSectionBlockSelect(block) {
    this._lockSlide(block.el.id);
  }

  onSectionBlockDeselect() {
    this._unlockSlide();
  }

  onSectionUnload() {
    this.events.forEach(({ el, event, listener }) => el.removeEventListener(event, listener));
    clearInterval(this.autoplayInterval);
  }

  // This function is for event handling. The hope is that this would be
  // moved into the SectionsManager class so it would be available to all
  // sections that we create. We could also have an unregisterEvent.
  _registerEvent(el, event, listener) {
    this.events.push({ el, event, listener });

    el.addEventListener(event, listener);

    return { el, event, listener };
  }

  _unregisterEvent(el, event, listener) {
    this.events = this.events.filter(e => el !== e.el
      || event !== e.event || listener !== e.listener);

    el.removeEventListener(event, listener);

    return { el, event, listener };
  }
}
